import { WhichWeightAPI } from 'WhichWeightAPI'
import { useState, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { GetPrimaryColorHex } from 'redux/slices/brand'
import { TrashIcon, PlusCircleIcon } from '@heroicons/react/24/outline'
import { Switch } from '@headlessui/react'
import moment from 'moment'

import BrandColorButton from 'components/Buttons/BrandColorButton'
import SolidButton from 'components/Buttons/SolidButton'
import ContentFrame from 'components/Frame/ContentFrame'
import PeriodizationSteps from 'components/ProgramGenerator/Periodization/PeriodizationSteps'
import PeriodizationVisualizer from 'components/ProgramGenerator/Periodization/PeriodizationVisualizer'

export default function PeriodizationGeneratorTab() {

    const styleConfig = useSelector((state) => state.brand.styleConfig)

    const abortControllerRef = useRef(null)
    const debounceTimerRef = useRef(null)

    const [programLengthInputValue, setProgramLengthInputValue] = useState(26)
    const [postRaceDeload, setPostRaceDeload] = useState(true)
    const [newEventName, setNewEventName] = useState('New Event')
    const [newEventType, setNewEventType] = useState('b-race')
    const [newWeekNumber, setNewWeekNumber] = useState(1)
    const [bAndCRaces, setBAndCRaces] = useState([])
    const [volumeProgressionModifier, setVolumeProgressionModifier] = useState(0)
    const [showPeriodizationSteps, setShowPeriodizationSteps] = useState(false)
    const [periodizationData, setPeriodizationData] = useState(null)
    
    const RandomizeInputs = () => {

        // 1. Generate random program length between 1 and 52

        const randomProgramLength = Math.floor(Math.random() * 52) + 1
        
        // 2. Random postRaceDeload boolean

        const randomPostRaceDeload = Math.random() < 0.5
        
        // 3. Generate 0-4 random events

        const numEvents = Math.floor(Math.random() * 5) // 0-4
        const randomEvents = []
        
        for (let i = 0; i < numEvents; i++) {
            // Random week number between 1 and program length
            const randomWeekNumber = Math.floor(Math.random() * randomProgramLength) + 1
            
            // Random event type
            const randomEventType = Math.random() < 0.5 ? 'b-race' : 'c-race'
            
            // Calculate event date based on week number (same logic as AddEvent)
            const eventDate = moment().local().add(randomWeekNumber - 1, 'week').format('YYYY-MM-DD')
            
            const newEvent = {
                id: i + 1,
                name: `Event ${i + 1}`,
                eventType: randomEventType,
                eventTrainingStartDate: eventDate,
                eventDate: eventDate,
                deloadAfterEvent: 0,
                programEndDate: eventDate,
                weekNumber: randomWeekNumber,
            }
            
            randomEvents.push(newEvent)
        }
        
        // 4. Generate volume progression modifier between 0 and program length
        
        const randomVolumeModifier = Math.floor(Math.random() * (randomProgramLength + 1))
        
        // Update all state values

        setProgramLengthInputValue(randomProgramLength)
        setPostRaceDeload(randomPostRaceDeload)
        setBAndCRaces(randomEvents)
        setVolumeProgressionModifier(randomVolumeModifier)
    }

    const AddEvent = () => {
        var tempEvents = JSON.parse(JSON.stringify(bAndCRaces))

        var eventDate = moment().local().add(newWeekNumber - 1, 'week').format('YYYY-MM-DD')

        var newEvent = {
            id: 1,
            name: newEventName,
            eventType: newEventType,
            eventTrainingStartDate: eventDate,
            eventDate: eventDate,
            deloadAfterEvent: 0,
            programEndDate: eventDate,
            weekNumber: newWeekNumber, // For display purposes only. Never used on the backend.
        }

        tempEvents.push(newEvent)

        setBAndCRaces(tempEvents)
    }

    const RemoveEvent = (index) => {
        
        var tempEvents = JSON.parse(JSON.stringify(bAndCRaces))

        tempEvents.splice(index, 1)
        
        setBAndCRaces(tempEvents)

    }

    const GetProgramPeriodizationFromBackend = async () => {
        // Cancel previous request if it exists
        if (abortControllerRef.current) {
            abortControllerRef.current.abort()
        }

        // Create new AbortController for this request
        const abortController = new AbortController()
        abortControllerRef.current = abortController

        setPeriodizationData(null)

        const jsonBody = {
            localDate: moment().local().format('YYYY-MM-DD'),
            programLength: programLengthInputValue,
            postRaceDeload: postRaceDeload,
            bAndCRaces: JSON.stringify(bAndCRaces),
            volumeProgressionModifier: volumeProgressionModifier
        }

        try {
            // console.log(jsonBody)

            const result = await WhichWeightAPI('PROGRAM', 'GET', '/program_periodization', jsonBody, { signal: abortController.signal })

            // Only update state if this request wasn't aborted
            if (!abortController.signal.aborted) {
                // console.log(result.data.data)
                setPeriodizationData(result.data.data)
            }
        } catch (error) {
            // Silently handle abort-related errors
            if (
                error.name === 'CanceledError' || 
                error.name === 'AbortError' || 
                error.code === 'ERR_CANCELED'
            ) {
                return
            }

            console.error('Error fetching periodization data:', error)
        }
    }

    const GetLoadingWeekBarStyle = (index) => {
        const isEven = index % 2 === 0
        const startHeight = isEven ? 64 : 52
        const endHeight = isEven ? 52 : 64
        const style = {
            className: 'flex items-end rounded bg-gray-500 w-6 loading-bar-wave',
            style: {
                '--start-height': `${startHeight}px`,
                '--end-height': `${endHeight}px`,
                height: `${startHeight}px`
            }
        }
        return style
    }

    useEffect(() => { 
        // Clear any existing debounce timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current)
        }

        // Debounce the API call - wait 300ms after slider stops moving
        debounceTimerRef.current = setTimeout(() => {
            GetProgramPeriodizationFromBackend()
        }, 300)

        // Cleanup: cancel any pending requests and timers when component unmounts or value changes
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current)
            }
            if (abortControllerRef.current) {
                abortControllerRef.current.abort()
            }
        }
    }, [programLengthInputValue, postRaceDeload, volumeProgressionModifier, bAndCRaces])

    const primaryColor = GetPrimaryColorHex(styleConfig)
    const fillPercentage = ((programLengthInputValue - 1) / (52 - 1)) * 100

    return (
        <>
            <div className='flex items-center gap-x-4'>
                <div className='w-40'>
                    <BrandColorButton 
                        size="extra-small"
                        text="Randomize Inputs"
                        action={() => RandomizeInputs()}
                        className='shadow-md'
                    />
                </div>

                <div className='w-40'>
                    <SolidButton
                        size="extra-small"
                        text="Regenerate"
                        action={() => GetProgramPeriodizationFromBackend()}
                        className='shadow-md'
                    />
                </div>

                <div className='flex-grow'/>

                <div 
                    className="flex items-center justify-center pl-3 py-1 border border-gray-300 rounded-full cursor-pointer bg-white shadow-md"
                    onClick={() => setShowPeriodizationSteps(!showPeriodizationSteps)}
                >  
                    <p className='text-xs w-[160px]'>Show Periodization Steps</p>

                    <div className="flex items-center justify-center py-1 pr-2">
                        <Switch
                            checked={showPeriodizationSteps}
                            className={`
                                relative inline-flex h-4 w-8 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out
                                ${showPeriodizationSteps ? 'bg-orange-600' : 'bg-gray-200'}
                            `}
                        >
                            <span className="sr-only">Use setting</span>
                            <span
                                aria-hidden="true"
                                className={`
                                    pointer-events-none inline-block h-3 w-3 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out
                                    ${showPeriodizationSteps ? 'translate-x-4' : 'translate-x-0'}
                                `}
                            />
                        </Switch>
                    </div>
                </div>
            </div>

            {/* Program Length Input */}

            <ContentFrame>
                <div className='w-full p-4'>
                    <div className="text-sm font-medium">Program Length: {programLengthInputValue}</div>

                    <style>
                        {`
                            @keyframes barPulse {
                                0%, 100% {
                                    height: var(--start-height);
                                }
                                50% {
                                    height: var(--end-height);
                                }
                            }
                            
                            .loading-bar-wave {
                                animation: barPulse 1.5s ease-in-out infinite;
                            }
                            
                            #program-length-slider {
                                -webkit-appearance: none;
                                appearance: none;
                                height: 8px;
                                outline: none;
                            }
                            
                            #program-length-slider::-webkit-slider-runnable-track {
                                height: 8px;
                                border-radius: 4px;
                                background: linear-gradient(to right, ${primaryColor} 0%, ${primaryColor} ${fillPercentage}%, #e5e7eb ${fillPercentage}%, #e5e7eb 100%);
                            }
                            
                            #program-length-slider::-webkit-slider-thumb {
                                -webkit-appearance: none;
                                appearance: none;
                                width: 20px;
                                height: 20px;
                                border-radius: 50%;
                                background: ${primaryColor};
                                cursor: pointer;
                                border: 2px solid white;
                                box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
                                margin-top: -6px;
                            }
                            
                            #program-length-slider::-webkit-slider-thumb:hover {
                                transform: scale(1.1);
                                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
                            }
                            
                            #program-length-slider::-moz-range-track {
                                height: 8px;
                                border-radius: 4px;
                                background-color: #e5e7eb;
                                border: none;
                            }
                            
                            #program-length-slider::-moz-range-progress {
                                height: 8px;
                                border-radius: 4px;
                                background-color: ${primaryColor};
                            }
                            
                            #program-length-slider::-moz-range-thumb {
                                width: 20px;
                                height: 20px;
                                border-radius: 50%;
                                background: ${primaryColor};
                                cursor: pointer;
                                border: 2px solid white;
                                box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
                            }
                            
                            #program-length-slider::-moz-range-thumb:hover {
                                transform: scale(1.1);
                                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
                            }
                        `}
                    </style>

                    <div className="flex items-center gap-x-4">
                        <input
                            id="program-length-slider"
                            type="range"
                            min="1"
                            max="52"
                            value={programLengthInputValue}
                            onChange={(e) => setProgramLengthInputValue(parseInt(e.target.value))}
                            className="w-full"
                        />

                        <div 
                            className="flex items-center justify-center pl-3 py-1 border border-gray-300 rounded-full cursor-pointer"
                            onClick={() => setPostRaceDeload(!postRaceDeload)}
                        >  
                            <p className='text-xs w-[110px]'>Post Race Deload</p>

                            <div className="flex items-center justify-center py-1 pr-2">
                                <Switch
                                    checked={postRaceDeload}
                                    className={`
                                        relative inline-flex h-4 w-8 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out
                                        ${postRaceDeload ? 'bg-orange-600' : 'bg-gray-200'}
                                    `}
                                >
                                    <span className="sr-only">Use setting</span>
                                    <span
                                        aria-hidden="true"
                                        className={`
                                            pointer-events-none inline-block h-3 w-3 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out
                                            ${postRaceDeload ? 'translate-x-4' : 'translate-x-0'}
                                        `}
                                    />
                                </Switch>
                            </div>
                        </div>
                    </div>
                </div>
            </ContentFrame>

            {/* Events Input */}

            <ContentFrame>
                <div className="w-full rounded-2xl border-t border-l border-gray-400">
                    <div className="flex border-b border-gray-400 font-semibold bg-gray-50 text-sm rounded-t-2xl">
                        <div className="flex-1 p-2 border-r border-gray-400">Event Name</div>

                        <div className="flex-1 p-2 border-r border-gray-400">Event Type</div>

                        <div className="flex-1 p-2 border-r border-gray-400">Week Number</div>

                        <div className="w-10 border-r border-gray-400" />
                    </div>

                    {/* Added Events */}
                    {bAndCRaces.length > 0 && (
                        <>
                            {bAndCRaces.map((event, index) => (
                                <div key={index} className="flex border-b border-gray-400">
                                    <div className="flex-1 p-2 border-r border-gray-400">{event.name}</div>

                                    <div className="flex-1 p-2 border-r border-gray-400">{event.eventType}</div>

                                    <div className="flex-1 p-2 border-r border-gray-400">{event.weekNumber}</div>

                                    <div 
                                        className="w-10 p-2.5 border-r border-gray-400 text-gray-600 cursor-pointer hover:text-gray-800" 
                                        onClick={() => RemoveEvent(index)}
                                    >
                                        <TrashIcon className="h-5" />
                                    </div>
                                </div>
                            ))}
                        </>
                    )}

                    {/* New Event Component */}
                    <div className="flex border-b border-gray-400 rounded-b-2xl">
                        <div className="flex-1 border-r border-gray-400">
                            <input 
                                type="text" 
                                name="eventName" 
                                id="eventName" 
                                placeholder="Event Name" 
                                className="w-full p-2" 
                                value={newEventName} 
                                onChange={(e) => setNewEventName(e.target.value)} 
                            />
                        </div>

                        <div className="flex-1 border-r border-gray-400">
                            <select 
                                id="sport" 
                                value={newEventType} 
                                onChange={(e) => setNewEventType(e.target.value)} 
                                className="w-full p-2 cursor-pointer"
                            >
                                <option value="b-race">B Race</option>
                                <option value="c-race">C Race</option>
                            </select>
                        </div>

                        <div className="flex-1 border-r border-gray-400">
                            <input 
                                type="number" 
                                name="weekNumber" 
                                id="weekNumber" 
                                placeholder="Week Number" 
                                value={newWeekNumber} 
                                onChange={(e) => setNewWeekNumber(e.target.value)} 
                                className="w-full p-2 cursor-pointer" 
                            />
                        </div>

                        <div 
                            className="w-10 p-2.5 border-r border-gray-400 text-gray-600 cursor-pointer hover:text-gray-800" 
                            onClick={() => AddEvent()}
                        >
                            <PlusCircleIcon className="h-5" />
                        </div>
                    </div>
                </div>
            </ContentFrame>

            {/* Volume Progression Modifier Input */}

            <ContentFrame>
                <div className='w-full p-4 space-y-4'>
                    <div>Volume Progression Modifier</div>

                    <input
                        min={0}
                        type="number"
                        name="volumeProgressionModifier"
                        id="volumeProgressionModifier"
                        className='w-full px-4 py-2 rounded-md text-gray-900 shadow-sm ring-1 ring-gray-500 placeholder:text-gray-400'
                        value={volumeProgressionModifier}
                        onChange={(event) => setVolumeProgressionModifier(parseInt(event.target.value))}
                    />
                </div>
            </ContentFrame>

            {/* Loading Animation */}

            {periodizationData === null && 
                <div className="pt-4 pb-0.5 px-4 space-y-4 bg-gradient-to-b from-zinc-800 to-zinc-600 rounded-xl">
                    <div className='text-center text-white'>
                        Loading...
                    </div>

                    <div>
                        <div className="flex justify-center">
                            <div className="inline-flex items-end gap-x-0.5 h-16">
                                {Array.from({ length: (programLengthInputValue + 4) }).map((_, index) => {
                                    const barStyle = GetLoadingWeekBarStyle(index)
                                    return (
                                        <div 
                                            key={index} 
                                            className={barStyle.className}
                                            style={barStyle.style}
                                        />
                                    )
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            }

            {/* Periodization Visualizers */}

            {periodizationData !== null && (
                <>
                    {showPeriodizationSteps && (
                        <>
                            <PeriodizationSteps
                                bAndCRaces={bAndCRaces}
                                volumeProgressionModifier={volumeProgressionModifier}
                                periodizationSteps={periodizationData.initialPeriodizationSteps}
                            />

                            {periodizationData.adjustedPeriodizationSteps !== null && (
                                <PeriodizationSteps
                                    labelPrefix="Adjusted"
                                    bAndCRaces={bAndCRaces}
                                    volumeProgressionModifier={volumeProgressionModifier}
                                    periodizationSteps={periodizationData.adjustedPeriodizationSteps}
                                />
                            )} 
                        </>
                    )}

                    <PeriodizationVisualizer
                        label="Final Periodization"
                        periodizationArray={periodizationData.finalPeriodization}
                    />
                </>
            )}
        </>
    )
}